<template>
    <div class="col-md-12">
        <page-title :title="$t('planning.planning')" :showTitle="false"></page-title>

        <div class="row mb-3">
            <div class="col-1">
                <input type="radio" name="loadType" :value="PlanningLoadType.Year" v-model="loadType" class="mt-3" />
            </div>
            <div class="col-11">
                <label class="font-weight-bold">{{ $t('planning.year') }}</label>
                <select class="form-control" v-model="selectedYear" :disabled="loadType === PlanningLoadType.Dates">
                    <option v-for="year in years" :value="year" :selected="year == currentYear">{{ year }}</option>
                </select>
                <div class="text-warning">{{ $t('planning.changingDataWarning') }}</div>
            </div>
        </div>

        <div class="mb-3 row">
            <div class="col-1">
                <input class="mt-3" type="radio" name="loadType" :value="PlanningLoadType.Dates" v-model="loadType" />
            </div>
            <div class="col-11 row">
                <div class="col-4">
                    <label class="font-weight-bold">{{ $t('planning.from') }}</label>
                    <DateOnlyPicker :show-time="false" v-model="loadFromDate" :readonly="loadType === PlanningLoadType.Year"></DateOnlyPicker>
                </div>
                <div class="col-4">
                    <label class="font-weight-bold">{{ $t('planning.to') }}</label>
                    <DateOnlyPicker :show-time="false" v-model="loadToDate" :readonly="loadType === PlanningLoadType.Year"></DateOnlyPicker>
                </div>
                <div class="col-4 mt-3">
                    <button class="btn btn-primary"
                            :disabled="loadType === PlanningLoadType.Year"
                            @click="loadByDates">{{ $t('planning.load') }}</button>
                </div>
                <div class="text-warning ml-3">{{ $t('planning.changingDataWarning') }}</div>
            </div>
        </div>

        <div class="loader" v-if="loading"></div>
        <div v-if="!loading">
            <!-- Filter -->
            <div class="card mb-3">
                <div class="card-header">
                    <div>
                        <strong class="h3">{{ $t('planning.filter') }}</strong>
                        <!--                        <button class="fas fa-arrow-left float-right text-primary"
                                                        data-toggle="collapse"
                                                        href="#filterBody"
                                                        role="button"
                                                        aria-expanded="false"
                                                        aria-controls="filterBody"></button>-->
                    </div>
                </div>
                <div class="card-body">
                    <div class="row col-12"> <!-- id="filterBody" class="row p-0" -->
                        <div class="col form-group">
                            <label class="font-weight-bold">{{ $t('thematicArea.theme') }}</label>
                            <input type="text" class="form-control" v-model="filter.theme" @input="filterItems">
                        </div>
                        <div class="col form-group">
                            <label class="font-weight-bold">{{ $t('planning.alternativeName') }}</label>
                            <input type="text" class="form-control" v-model="filter.alternativeName" @input="filterItems">
                        </div>
                        <div class="col form-group">
                            <label class="font-weight-bold">{{ $t('thematicArea.thematicArea') }}</label>
                            <dynamic-dropdown api-url="/"
                                              v-if="loadedThematicAreasDropdownItems"
                                              :items-from-parent="thematicAreasDropdownItems"
                                              :required="false"
                                              :field-name="$t('thematicArea.thematicArea')"
                                              v-model="filter.thematicAreaId"></dynamic-dropdown>
                        </div>
                        <div class="col form-group">
                            <label class="font-weight-bold">{{ $t('planning.legalArea') }}</label>
                            <nomenclature-dropdown :load-by-parent-sort="true"
                                                   :parent-sort="nomenclature.LegalArea"
                                                   :required="false"
                                                   :field-name="$t('planning.legalArea')"
                                                   v-model="filter.legalAreaId" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    <div class="h5">
                        {{ $t('planning.planning') }} - {{ planning.year }}
                        <select class="form-control" v-model="planning.year">
                            <option v-for="(year, index) in years" :key="index" :value="year" :selected="year == planning.year">{{ year }}</option>
                        </select>
                    </div>
                </div>
                <div class="card-body">
                    <div v-if="planning.items.length < 1">
                        {{ $t('planning.noThemesFound') }}
                        <!--{{ $t('planning.haveNo') }} {{ $t(planning.themes) }}-->
                        <!--{{ $t('planning._for') }} {{ selectedYear }} {{ $t('planning.year').toLowerCase() }}-->
                        <br>
                        <button v-if="hasCreateRights" class="btn btn-primary" data-toggle="modal"
                                data-target="#themeProposalModalClosed">{{ $t('add') }}
                        </button>
                    </div>
                    <table v-else class="table table-bordered table-responsive">
                        <thead>
                        <tr>
                            <th>{{ $t('planning.includeInAnnualCatalog') }}</th>
                            <th class="required">{{ $t('planning.theme') }}</th>
                            <th>{{ $t('planning.alternativeName') }}</th>
                            <th class="required" style="width: 20%; max-width: 20%;">{{ $t('thematicArea.thematicArea') }}
                            </th>
                            <th class="required" width="20%">{{ $t('planning.legalArea') }}</th>
                            <th>{{ $t('description') }}</th>
                            <th>{{ $t('planning.lectorsProposal') }}</th>
                            <th>{{ $t('planning.passEventsForTheme') }}</th>
                            <th>{{ $t('planning.passEventsLectorsForTheme') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <template v-for="item of planning.items">
                            <planning-item v-if="loadedThematicAreasDropdownItems"
                                           :item="item"
                                           :thematic-areas-dropdown-items="thematicAreasDropdownItems"></planning-item>
                            <div v-else class="smallLoader"></div>
                        </template>
                        </tbody>
                        <tfoot>
                            <tr colspan="6">
                                <td>
                                    <button v-if="hasCreateRights" class="btn btn-primary" data-toggle="modal"
                                            data-target="#themeProposalModalClosed">{{ $t('add') }}
                                    </button>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                    <div class="row">
                        <ul class="col pagination justify-content-start" v-if="pagination.allPages > 0">
                            <li class="page-item">
                                <a href="#" class="page-link" @click.prevent="pageChanged(0)">
                                    <i class="fas fa-angle-double-left"></i>
                                </a>
                            </li>
                            <li class="page-item">
                                <a href="#" class="page-link" :readonly="pagination.currentPage == 0" @click.prevent="pageChanged(pagination.currentPage - 1)">
                                    <i class="fas fa-caret-left"></i>
                                </a>
                            </li>
                            <li class="page-item">
                                <a href="#" class="page-link" @click.prevent="pagination.currentPage = pagination.currentPage">{{ pagination.currentPage + 1 }}</a>
                            </li>
                            <li v-if="pagination.currentPage != pagination.allPages - 1" class="page-item">
                                <a href="#" class="page-link" @click.prevent="pageChanged(pagination.currentPage + 1)">
                                    <i class="fas fa-caret-right"></i>
                                </a>
                            </li>
                            <li class="page-item">
                                <a href="#" class="page-link" @click.prevent="pageChanged(pagination.allPages)">
                                    <i class="fas fa-angle-double-right"></i>
                                </a>
                            </li>
                        </ul>
                        <div class="col">
                            <div class="float-right text-muted">
                                {{ (pagination.currentPage + 1) * pagination.itemsPerPage  - pagination.itemsPerPage }}
                                -
                                {{ (pagination.currentPage) * pagination.itemsPerPage + planning.items.length  }}
                                /
                                {{ pagination.allItemsCount }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="text-center mt-3" v-if="!loading">
                <button v-if="hasCreateRights" class="btn btn-primary mr-3" @click="onSave">
                    <i class="fas fa-save"></i>
                    {{ $t('planning.addToAnnualCatalogProject') }}
                </button>
            </div>
        </div>

        <!-- Add theme proposal modal -->
        <div class="modal fade" id="themeProposalModalClosed" tabindex="-1" role="dialog" aria-hidden="true"
             data-keyboard="false" data-backdrop="static">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="exampleModalLabel">{{ $t('thematicArea.thematicAreaProposal') }}</h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close"
                                @click="themeProposalModalClosed">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="form-group col-md-12">
                            <label class="font-weight-bold required">{{ $t('thematicArea.theme') }}</label>
                            <input class="form-control"
                                   id="thematicArea"
                                   :name="'thematicArea'"
                                   :data-vv-as="$t('thematicArea.theme')"
                                   v-model="themeProposal.theme">
                            <span class="text-danger col-12" v-if="!isValidProposalTheme">{{ $t('validation.requiredField') }}</span>
                            <span class="text-danger col-12" v-if="!isThemeNameUnique">{{ $t('validation.themeProposalUniqueName') }}</span>
                        </div>
                        <div class="form-group col-md-12">
                            <label class="font-weight-bold required">{{ $t('registers.lectors.thematicArea') }}</label>
                            <dynamic-dropdown api-url="/api/Dropdown/GetThematicAreas"
                                              :items="thematicAreasDropdownItems"
                                              v-model="themeProposal.thematicAreaId"></dynamic-dropdown>
                            <span class="text-danger col-12" v-if="!isValidProposalThematicArea">{{ $t('validation.requiredField') }}</span>
                        </div>
                        <div class="form-group col-md-12">
                            <label class="font-weight-bold required">{{ $t('thematicArea.legalArea') }}</label>
                            <dropdown :load-by-parent-sort="true"
                                      :parent-sort="nomenclature.LegalArea"
                                      :fieldName="$t('thematicArea.legalArea')"
                                      v-model="themeProposal.legalAreaId"></dropdown>
                            <span class="text-danger col-12" v-if="!isValidProposalLegalArea">{{ $t('validation.requiredField') }}</span>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="themeProposalModalClosed"
                                data-dismiss="modal">{{ $t('buttons.cancel') }}
                        </button>
                        <button v-if="hasCreateRights" type="button" class="btn btn-primary" @click="addItem">
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { PlanningModel, PlanningItemModel } from '@/models/models';
    import PageTitle from '@/components/common/PageTitle';
    import { NomenclatureParentBySort, PersonSearchType, PlanningLoadType } from '@/models/enums';
    import PlanningItem from '@/components/planning/PlanningItem';
    import Dropdown from '@/components/dropdown/NomenclatureDropdown';
    import DynamicDropdown from '@/components/dropdown/DynamicDropdown';
    import NomenclatureDropdown from '@/components/dropdown/NomenclatureDropdown';
    import DateOnlyPicker from '@/components/datepicker/DateOnlyPicker';

    export default {
        inject: ['$validator'],
        components: {
            DateOnlyPicker,
            PageTitle,
            PlanningItem,
            Dropdown,
            DynamicDropdown,
            NomenclatureDropdown
        },
        data() {
            return {
                id: '',
                loading: true,
                personSearchType: PersonSearchType,
                currentYear: new Date().getFullYear(),
                selectedYear: new Date().getFullYear(),
                planning: new PlanningModel({year: new Date().getFullYear() + 1}),
                allItems: [],
                approvedItems: [],
                years: [],
                thematicAreasDropdownItems: [],
                loadedThematicAreasDropdownItems: false,
                loadType: PlanningLoadType.Year,
                loadFromDate: '',
                loadToDate: '',
                PlanningLoadType: PlanningLoadType,
                themeProposal: {
                    theme: '',
                    thematicAreaId: '',
                    legalAreaId: ''
                },
                filter: {
                    theme: '',
                    alternativeName: '',
                    thematicAreaId: '',
                    legalAreaId: ''
                },
                pagination: {
                    currentPage: 0,
                    allPages: 0,
                    itemsPerPage: 15,
                    allItemsCount: 0
                },
                nomenclature: NomenclatureParentBySort
            }
        },
        methods: {
            addItem() {
                if (this.themeProposal.theme.length > 0 && this.themeProposal.legalAreaId && this.themeProposal.thematicAreaId && this.isThemeNameUnique) {
                    this.themeProposal.year = this.selectedYear;
                    this.$http.post('/api/ThematicAreaProposal/AddProposal', this.themeProposal)
                        .then(result => {
                            $('#themeProposalModalClosed').modal('hide');
                            this.$vs.notify({
                                title: this.$t('notifications.success'),
                                text: this.$t('notifications.successfulSavedData'),
                                position: 'top-center',
                                color: 'success'
                            });

                            let newItem = new PlanningItemModel({
                                thematicAreaId: this.themeProposal.thematicAreaId,
                                legalAreaId: this.themeProposal.legalAreaId,
                                theme: this.themeProposal.theme
                            });

                            this.planning.items.push(newItem);
                            this.allItems.push(newItem);
                        })
                        .catch((error => {
                            $('#themeProposalModalClosed').modal('hide');
                            this.$vs.notify({
                                title: this.$t('notifications.error'),
                                text: this.$t('notifications.errorDataNotSaved'),
                                position: 'top-center',
                                color: 'danger'
                            });
                        }));
                }
            },
            themeProposalModalClosed() {
                this.themeProposal.theme = '';
                this.themeProposal.legalAreaId = '';
                this.year = '';
                this.thematicAreaId = '';
            },
            loadThematicAreaDropdowns() {
                this.$http.get(`/api/Dropdown/GetThematicAreas`).then(result => {
                    this.items = [];
                    result.data.forEach(item => {
                        this.thematicAreasDropdownItems.push({
                            id: item.id,
                            text: this.isEnVersion && item.textEn ? item.textEn : item.text,
                            isRegisterField: item.isRegisterField
                        })
                    });
                    this.loadedThematicAreasDropdownItems = true;
                    this.items = result.data;
                    this.loading = false;
                }).catch(error => {
                    // TODO: Toast
                });
            },
            loadProposedThemes() {
                if (this.loadType === this.PlanningLoadType.Year) {
                    this.loading = true;
                    this.planning.items = [];
                    this.$http.get(`/api/Planning/GetThemesForPlanning/${this.selectedYear}/${this.pagination.currentPage}/${this.pagination.itemsPerPage}`).then(result => {
                        this.pagination.allPages = result.data.allPages;
                        this.pagination.allItemsCount = result.data.allItemsCount;
                        result.data.items.forEach(item => {
                            let newItem = new PlanningItemModel(item);
                            this.planning.items.push(newItem);
                            this.allItems.push(newItem);
                        });
                        this.checkForAlreadyApprovedItems();
                        this.loading = false;
                    }).catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('notifications.error'),
                            text: this.$t('notifications.errorLoadingData'),
                            position: 'top-center',
                            color: 'danger'
                        });
                    });
                }
            },
            loadByDates() {
                this.loading = true;
                this.planning.items = [];
                this.$http.get(`/api/Planning/GetThemesForPlanningByDates/${this.loadFromDate}/${this.loadToDate}/${this.pagination.currentPage}/${this.pagination.itemsPerPage}`).then(result => {
                    this.pagination.allPages = result.data.allPages;
                    result.data.items.forEach(item => {
                        let newItem = new PlanningItemModel(item);
                        this.planning.items.push(newItem);
                        this.allItems.push(newItem);
                    });
                    this.checkForAlreadyApprovedItems();
                    this.loading = false;
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.error'),
                        text: this.$t('notifications.errorLoadingData'),
                        position: 'top-center',
                        color: 'danger'
                    });
                });
            },
            pageChanged(page) {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.pagination.currentPage = page;
                        this.addRemoveFromApproved();
                        if (this.loadType === PlanningLoadType.Dates) {
                            this.loadByDates();
                        } else {
                            this.loadProposedThemes();
                        }
                    } else {
                        this.$vs.notify({
                            title: this.$t('planning.validationError'),
                            text: this.$t('planning.pleaseFixValidationErrors'),
                            position: 'top-center',
                            color: 'warning'
                        });
                    }
                });
            },
            addRemoveFromApproved() {
                const checked = this.planning.items.filter(i => i.includeInAnnualCatalog);
                if (checked) {
                    checked.forEach(newItem => {
                        this.approvedItems.push(new PlanningItemModel(newItem));
                    })
                }
                const unchecked = this.planning.items.filter(i => i.includeInAnnualCatalog == false);
                if (unchecked) {
                    unchecked.forEach(item => {
                        const itemToRemoveIndex = this.approvedItems.findIndex(i => i.theme == item.theme);
                        if (itemToRemoveIndex > -1) {
                            this.approvedItems.splice(itemToRemoveIndex, 1);
                        }
                    })
                }
            },
            checkForAlreadyApprovedItems() {
                this.planning.items.forEach(item => {
                    const alreadyApprovedItemIndex = this.approvedItems.findIndex(i => i.theme == item.theme);
                    if (alreadyApprovedItemIndex > -1) {
                        const approvedItem = this.approvedItems[alreadyApprovedItemIndex];
                        item.includeInAnnualCatalog = true;
                        item.alternativeName = approvedItem.alternativeName;
                        item.thematicAreaId = approvedItem.thematicAreaId;
                        item.legalAreaId = approvedItem.legalAreaId;
                        item.proposedLectorsIds = approvedItem.proposedLectorsIds;
                        item.lectors = approvedItem.lectors;
                    }
                });
            },
            onSave() {
                if (!this.hasCreateRights) {
                    return;
                }

                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.createAnnualCatalogProject();
                    }
                });
            },
            createAnnualCatalogProject() {
                this.loading = true;
                var objToSend = {
                    year: this.planning.year,
                    items: this.approvedItems
                }
                this.$http.post('/api/AnnualCatalogProject/Create', objToSend).then(result => {
                    this.loading = false;
                    this.id = result.data;
                    this.$router.push(`/AnnualCatalogProject/${this.id}`);
                    this.$vs.notify({
                        title: this.$t('notifications.success'),
                        text: this.$t('notifications.successfulSavedData'),
                        position: 'top-center',
                        color: 'success'
                    });
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.error'),
                        text: this.$t('notifications.errorDataNotSaved'),
                        position: 'top-center',
                        color: 'danger'
                    });
                })
            },
            loadYears() {
                let currentYear = this.currentYear;
                const years = 50;
                for (let i = currentYear - years; i <= currentYear + years; i++) {
                    this.years.push(i);
                }
            },
            filterItems() {
                this.planning.items = this.allItems.filter(item => {
                    const passThemeFilter = item.theme.toLowerCase().includes(this.filter.theme.toLowerCase());
                    const passAlternativeNameFilter = item.alternativeName.toLowerCase().includes(this.filter.alternativeName.toLowerCase());

                    let passThematicAreaFilter = true;
                    if (this.filter.thematicAreaId) {
                        passThematicAreaFilter = (item.thematicAreaId === this.filter.thematicAreaId);
                    }

                    let passLegalAreaFilter = true;
                    if (this.filter.legalAreaId && !isNaN(parseInt(this.filter.legalAreaId))) {
                        passLegalAreaFilter = item.legalAreaId === parseInt(this.filter.legalAreaId);
                    }

                    return (passThemeFilter && passAlternativeNameFilter && passThematicAreaFilter && passLegalAreaFilter);
                });
            }
        },
        watch: {
            selectedYear: function (newVal, oldVal) {
                // this.planning.year = this.selectedYear;
                this.loadProposedThemes();
            },
            'filter.thematicAreaId': function (newVal, oldVal) {
                this.filterItems();
            },
            'filter.legalAreaId': function(newVal, oldVal) {
                this.filterItems();
            }
        },
        computed: {
            isValidProposalTheme: function () {
                return this.themeProposal.theme.trim().length > 0;
            },
            isValidProposalThematicArea: function () {
                return !isNaN(parseInt(this.themeProposal.thematicAreaId));
            },
            isValidProposalLegalArea: function () {
                return !isNaN(parseInt(this.themeProposal.legalAreaId));
            },
            hasCreateRights() {
                return this.$store.getters.claims.planning.create;
            },
            hasEditRights() {
                return this.$store.getters.claims.planning.update;
            },
            isThemeNameUnique() {
                let isUnique = true;
                let items = this.planning.items;
                for (let i = 0; i < items.length; i++) {
                    if (items[i].theme.trim().toLowerCase() === this.themeProposal.theme.trim().toLowerCase()) {
                        isUnique = false;
                        break;
                    }
                }
                return isUnique;
            }
        },
        mounted() {
            this.loadThematicAreaDropdowns();
            this.loadYears();
            this.loadProposedThemes();
        }
    }
</script>

<style scoped>
thead tr:nth-child(1) th {
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
}
.table-responsive {
    height:400px;
    overflow:scroll;
}
</style>
